/**
 * BarrelSaunaEngine – Bastutunna
 * Cylindrical barrel sauna with curved stave walls, flat end caps, and cradle supports.
 *
 * A barrel sauna (bastutunna) is built from staves (planks) that form a cylinder,
 * held together by steel bands. It sits on wooden cradle supports.
 *
 * Dimensions:
 *   width  = diameter of the barrel (m)  — typically 1.8–2.4 m
 *   length = total length of the barrel (m) — typically 2.0–4.0 m
 */

import * as THREE from "three";
import {
	type BuildGuideModifierContext,
	type BuildGuideText,
	buildGuideText,
	type ProjectBuildGuideStepDraft,
} from "../domain/build/buildGuideDraft";
import { getDeckFootprintRects } from "../domain/deck/deckFootprint";
import { calculateFoundation } from "../domain/foundation/calculateFoundation";
import { getBarrelAuditMetadata, getBarrelEvidenceConfig } from "./barrelAudit";
import { createCameraPosition, createCameraPreset } from "./cameraFraming";
import { chimneyStackFor } from "./chimneyInput";
import { buildChimneyStack, CHIMNEY_OUTER_RADIUS_M } from "./chimneyGeometry";
import { setConcreteContactSpan } from "./concreteVariation";
import { BARREL_DEFAULT_DIMENSIONS } from "./defaultDimensions";
import {
	addOpeningTrimFrame,
	addSlattedBench,
	addWallVent,
} from "./detailHelpers";
import {
	createFrontVisibilityGroup,
	type FrontVisibilityGroups,
} from "./frontVisibility";
// ─── Helpers (shared) ───────────────────────────────────────
import { addBox } from "./geometry";
import {
	buildHeaterGroup,
	getHeaterHeight as getHeaterHeightM,
	getHeaterWidth,
} from "./heaterGeometry";
// ─── Materials (centralized PBR library) ────────────────────
import {
	matBand,
	matBarrelEndCap,
	matBench,
	matConcrete,
	matCornerTrim,
	matCornerTrimHorizontal,
	matCradle,
	matEndCap,
	matGlass,
	matMetal,
	matRoofFlashing,
	matShield,
	matStave,
	matTreatedWood,
} from "./materials";
import type {
	BuildInput,
	BuildResult,
	CutRow,
	MaterialRow,
	RawCalculationResult,
	RawSaunaEngine,
} from "./types";

const FOUNDATION_BEAM_WIDTH = 0.045;
const FOUNDATION_BEAM_HEIGHT = 0.145;
const PLINTH_VISIBLE_HEIGHT = 0.4;
const PLINTH_RADIUS = 0.08;

function resolveBarrelSupportLayout(W: number, L: number, input: BuildInput) {
	const foundation = calculateFoundation(
		W,
		L,
		input.siteConditions,
		input.overrides,
	);
	return {
		foundation,
		cradleRows: foundation.beams.map((beam) => beam.z),
	};
}

function addBarrelEntrySteps(
	foundationGroup: THREE.Group,
	entryDoorCenterX: number,
	frontZ: number,
	saunaDimensions: BuildInput["dimensions"],
	deckConfig?: BuildInput["deckConfig"],
) {
	const entryFrontExtension =
		deckConfig?.attachWall === "front"
			? Math.max(
					0,
					...getDeckFootprintRects(deckConfig, saunaDimensions).map(
						(rect) => rect.centerZ + rect.depth / 2,
					),
				)
			: 0;
	const stepFrontZ = frontZ + entryFrontExtension;

	addBox(
		0.72,
		0.065,
		0.26,
		matCradle,
		entryDoorCenterX,
		0.0325,
		stepFrontZ + 0.2,
		foundationGroup,
	).name = "detail/entry/barrel-upper-step";
	addBox(
		0.58,
		0.055,
		0.24,
		matCradle,
		entryDoorCenterX,
		-0.06,
		stepFrontZ + 0.4,
		foundationGroup,
	).name = "detail/entry/barrel-lower-step";
	addBox(
		0.46,
		0.05,
		0.22,
		matCradle,
		entryDoorCenterX,
		-0.145,
		stepFrontZ + 0.58,
		foundationGroup,
	).name = "detail/entry/barrel-base-step";
}

// ─── Build ──────────────────────────────────────────────────
function buildBarrel3D(input: BuildInput): BuildResult {
	const {
		dimensions: { width: W, length: L },
	} = input;
	const R = W / 2; // barrel radius
	const saunaGroup = new THREE.Group();
	const foundationGroup = new THREE.Group();
	const deckGroup = new THREE.Group();
	const framingGroup = new THREE.Group(); // staves
	const panelGroup = new THREE.Group(); // bands + cradles
	const roofGroup = new THREE.Group(); // (not used — barrel is the roof)
	const interiorGroup = new THREE.Group();
	const exteriorGroup = new THREE.Group();
	const frontVisibilityGroups: FrontVisibilityGroups = {
		framing: createFrontVisibilityGroup("framing", framingGroup),
		panel: createFrontVisibilityGroup("panel", panelGroup),
		roof: createFrontVisibilityGroup("roof", roofGroup),
		interior: createFrontVisibilityGroup("interior", interiorGroup),
		exterior: createFrontVisibilityGroup("exterior", exteriorGroup),
	};

	const { foundation, cradleRows } = resolveBarrelSupportLayout(W, L, input);

	const cradleH = 0.12;
	const barrelCenterY = R + cradleH;
	const entryDoorCenterX = 0;
	const beamY = -FOUNDATION_BEAM_HEIGHT / 2;

	foundation.beams.forEach((beam, index) => {
		const support = addBox(
			beam.length,
			FOUNDATION_BEAM_HEIGHT,
			FOUNDATION_BEAM_WIDTH,
			matTreatedWood,
			beam.x,
			beamY,
			beam.z,
			foundationGroup,
		);
		support.name = `detail/foundation/beam-${index + 1}`;
	});

	const plinthY = -FOUNDATION_BEAM_HEIGHT - PLINTH_VISIBLE_HEIGHT / 2;
	foundation.plinths.forEach((plinth, index) => {
		const pierGeometry = new THREE.CylinderGeometry(
			PLINTH_RADIUS,
			PLINTH_RADIUS + 0.02,
			PLINTH_VISIBLE_HEIGHT,
			16,
		);
		// Lets matConcrete put its ground-contact band on the foot.
		setConcreteContactSpan(pierGeometry, PLINTH_VISIBLE_HEIGHT);
		const pier = new THREE.Mesh(pierGeometry, matConcrete);
		pier.position.set(plinth.x, plinthY, plinth.z);
		pier.castShadow = true;
		pier.receiveShadow = true;
		pier.name = `detail/foundation/plinth-${index + 1}`;
		foundationGroup.add(pier);

		addBox(
			FOUNDATION_BEAM_WIDTH + 0.01,
			0.1,
			0.05,
			matMetal,
			plinth.x,
			beamY,
			plinth.z,
			foundationGroup,
		).name = `detail/foundation/shoe-${index + 1}`;
	});

	// 1. BARREL CYLINDER (stave shell)
	// DoubleSide so the shell is visible from both outside and inside
	const barrelGeom = new THREE.CylinderGeometry(R, R, L, 32, 1, true);
	// The shared material, NOT a clone: a clone never sees
	// `applyFinishSelection`, so the shell would keep the previous colour for
	// the rest of the session (treatment changes do not dirty the geometry).
	const barrel = new THREE.Mesh(barrelGeom, matStave);
	barrel.rotation.x = Math.PI / 2;
	barrel.position.set(0, barrelCenterY, 0);
	barrel.castShadow = true;
	barrel.receiveShadow = true;
	barrel.name = "detail/barrel-shell/main";
	framingGroup.add(barrel);

	// 2. END CAPS (circular discs)
	// DoubleSide so caps are visible from both outside and inside the barrel
	const capGeom = new THREE.CircleGeometry(R - 0.02, 32);
	const capMat = matBarrelEndCap;

	// Back end cap (solid)
	const backCap = new THREE.Mesh(capGeom, capMat);
	backCap.position.set(0, barrelCenterY, -L / 2);
	backCap.castShadow = true;
	backCap.name = "detail/barrel-end-cap/back";
	framingGroup.add(backCap);

	// Front end cap (with door opening) — simplified as solid with glass overlay
	const frontCapOuter = new THREE.Mesh(capGeom, capMat);
	frontCapOuter.position.set(0, barrelCenterY, L / 2);
	frontCapOuter.rotation.y = Math.PI;
	frontCapOuter.castShadow = true;
	frontCapOuter.name = "detail/barrel-end-cap/front";
	frontVisibilityGroups.framing!.add(frontCapOuter);

	const trimDepth = 0.05;

	// Door (glass panel on front). The leaf used to be created without a name,
	// which made it invisible to every audit that walks the scene by mesh name —
	// the BOM/geometry parity check reported the barrel as "sells a door, draws
	// none" when the door was there all along. Anonymous geometry cannot be
	// verified, so name it like the rectangular engines do.
	const doorW = 0.7,
		doorH = 1.6;
	addBox(
		doorW,
		doorH,
		0.03,
		matGlass,
		0,
		cradleH + doorH / 2 + 0.05,
		L / 2 + 0.01,
		frontVisibilityGroups.exterior!,
	).name = "detail/procedural-door-leaf/barrel-door";
	addOpeningTrimFrame({
		group: frontVisibilityGroups.exterior!,
		width: doorW,
		height: doorH,
		depth: trimDepth,
		trimWidth: 0.045,
		material: matCornerTrim,
		horizontalMaterial: matCornerTrimHorizontal,
		position: new THREE.Vector3(0, cradleH + doorH / 2 + 0.05, L / 2 + 0.03),
		orientation: "z",
		namePrefix: "barrel-door",
	});
	addBarrelEntrySteps(
		foundationGroup,
		entryDoorCenterX,
		L / 2,
		input.dimensions,
		input.deckConfig,
	);

	// 3. STEEL BANDS
	const numBands = Math.max(2, Math.floor(L / 0.8));
	const bandSpacing = L / (numBands + 1);
	for (let i = 1; i <= numBands; i++) {
		const bandGeom = new THREE.TorusGeometry(R + 0.01, 0.015, 8, 32);
		const band = new THREE.Mesh(bandGeom, matBand);
		band.position.set(0, barrelCenterY, -L / 2 + i * bandSpacing);
		band.castShadow = true;
		band.name = `detail/barrel-band/${i}`;
		panelGroup.add(band);
	}

	for (const z of [-L / 2 + 0.012, L / 2 - 0.012]) {
		const endRing = new THREE.Mesh(
			new THREE.TorusGeometry(R - 0.03, 0.02, 8, 32),
			matCornerTrim,
		);
		endRing.position.set(0, barrelCenterY, z);
		endRing.castShadow = true;
		endRing.name = `detail/silhouette/barrel-${z < 0 ? "back" : "front"}-rim`;
		(z < 0 ? panelGroup : frontVisibilityGroups.panel!).add(endRing);
	}

	// 4. CRADLE SUPPORTS
	for (const [index, z] of cradleRows.entries()) {
		// Cradle base (cross-beam under the barrel)
		addBox(
			W + 0.2,
			cradleH,
			0.08,
			matCradle,
			0,
			cradleH / 2,
			z,
			panelGroup,
		).name = `detail/barrel-cradle/base-${index + 1}`;
		// Cradle side arms — reach up to barrel center to properly cup the barrel
		const armH = R * 1.1;
		const armY = cradleH + armH / 2;
		addBox(0.08, armH, 0.08, matCradle, -R - 0.04, armY, z, panelGroup).name =
			`detail/barrel-cradle/left-${index + 1}`;
		addBox(0.08, armH, 0.08, matCradle, R + 0.04, armY, z, panelGroup).name =
			`detail/barrel-cradle/right-${index + 1}`;
	}

	// 5. FLOOR inside barrel (flat platform at bottom)
	const floorW = W * 0.7;
	addBox(
		floorW,
		0.03,
		L - 0.1,
		// The interior floor: NOT facade, so it keeps its own tint when the
		// barrel is painted.
		matEndCap,
		0,
		cradleH +
			0.015 -
			(R - Math.sqrt(R * R - (floorW / 2) * (floorW / 2))) +
			R * 0.15,
		0,
		interiorGroup,
	).name = "detail/floor-panel/barrel-main";

	const floorY = cradleH + R * 0.15;

	// 6. HEATER + CHIMNEY  (placed FIRST so bench layout can account for clearance)
	// Real barrel saunas: heater sits on the floor at the very back, centered.
	// Benches are positioned forward of the heater with clearance.
	// The barrel hardcoded "wood" here, so an electric build rendered a cast-iron
	// stove (and, below, a chimney) regardless of what the user chose.
	const heaterType = input.thermalInputs.heaterType;
	const heaterW = getHeaterWidth(input.thermalInputs);
	const heaterH = getHeaterHeightM(input.thermalInputs);
	const hX = 0; // centered in barrel
	const hZ = -L / 2 + heaterW / 2 + 0.15; // tight to back wall
	const heaterClearanceZ = hZ + heaterW / 2 + 0.15; // Z where benches can safely start
	buildHeaterGroup(
		input.thermalInputs,
		new THREE.Vector3(hX, floorY + heaterH / 2, hZ),
		interiorGroup,
		"barrel",
		0,
	);
	// Heat shield on back wall behind heater
	addBox(
		heaterW + 0.15,
		0.8,
		0.02,
		matShield,
		hX,
		floorY + 0.4,
		-L / 2 + 0.03,
		interiorGroup,
	).name = "detail/shield/barrel-back";

	// 7. BENCHES — two floor-level benches along left & right walls
	// Simple flat benches on the floor, one on each side. No upper lavar.
	const benchLength = L / 2 - (heaterClearanceZ - -L / 2) - 0.1;
	const benchCenterZ = heaterClearanceZ + benchLength / 2;
	const benchDepth = 0.45; // seat depth
	const benchX = floorW / 2 - benchDepth / 2 - 0.02;

	for (const side of [-1, 1] as const) {
		addSlattedBench({
			group: interiorGroup,
			width: benchLength,
			depth: benchDepth,
			height: 0.035,
			legHeight: 0.18,
			slatCount: 5,
			material: matBench,
			position: new THREE.Vector3(
				side * benchX,
				floorY + 0.18 + 0.035 / 2,
				benchCenterZ,
			),
			namePrefix: `barrel-bench-${side < 0 ? "left" : "right"}`,
			orientation: "z",
		});
	}

	// Only a wood-fired stove has a flue. The BOM already branches on heater type
	// (see the electric rows below), but the builder drew the whole chimney stack
	// unconditionally — an electric barrel rendered with a pipe through the roof.
	if (heaterType === "wood") {
		// Roof penetration at the very top of the barrel shell.
		const roofY = barrelCenterY + R;
		buildChimneyStack({
			group: interiorGroup,
			namePrefix: "barrel",
			x: hX,
			z: hZ,
			floorY,
			stack: chimneyStackFor(input),
		});

		// Flashing: a patch of the barrel's own cylinder, so it lies on the
		// staves instead of bridging them (a flat plate this wide floats ~3 cm
		// at its edges on a 1 m radius).
		const flashingSize = 2 * CHIMNEY_OUTER_RADIUS_M + 0.2;
		const flashingThickness = 0.006;
		const halfArc = flashingSize / 2 / R;
		const flashingPlate = new THREE.Mesh(
			new THREE.CylinderGeometry(
				R + flashingThickness / 2,
				R + flashingThickness / 2,
				flashingSize,
				12,
				1,
				true,
				Math.PI - halfArc,
				2 * halfArc,
			),
			matRoofFlashing,
		);
		flashingPlate.rotation.x = Math.PI / 2;
		flashingPlate.position.set(hX, barrelCenterY, hZ);
		flashingPlate.castShadow = true;
		flashingPlate.name = "detail/chimney/barrel-roof-flashing";
		interiorGroup.add(flashingPlate);

		const collarR = CHIMNEY_OUTER_RADIUS_M + 0.02;
		const collarH = 0.025;
		const stormCollar = new THREE.Mesh(
			new THREE.CylinderGeometry(collarR, collarR + 0.008, collarH, 32),
			matRoofFlashing,
		);
		stormCollar.position.set(hX, roofY + flashingThickness + collarH / 2, hZ);
		stormCollar.castShadow = true;
		stormCollar.name = "detail/chimney/barrel-storm-collar";
		interiorGroup.add(stormCollar);
	}

	// 9. VENTILATION — intake near heater (back end cap), exhaust on the front
	// end cap. Both are plate valves on the cap's inner face, pointing into the
	// room; the blue/red supply/exhaust coding is added by the ventilation
	// overlay, never here (see materials.ts).
	addWallVent({
		group: interiorGroup,
		position: new THREE.Vector3(
			-heaterW / 2 - 0.2,
			floorY + 0.12,
			-L / 2 + 0.02,
		),
		facing: "+z",
		kind: "supply",
		name: "barrel-intake",
	});
	addWallVent({
		group: interiorGroup,
		position: new THREE.Vector3(
			-floorW / 2 + 0.05,
			floorY + R * 0.55,
			L / 2 - 0.02,
		),
		facing: "-z",
		kind: "exhaust",
		name: "barrel-exhaust",
	});

	saunaGroup.add(foundationGroup);
	if (deckGroup.children.length > 0) {
		saunaGroup.add(deckGroup);
	}
	saunaGroup.add(framingGroup);
	saunaGroup.add(panelGroup);
	saunaGroup.add(roofGroup);
	saunaGroup.add(interiorGroup);
	saunaGroup.add(exteriorGroup);

	return {
		saunaGroup,
		foundationGroup,
		deckGroup,
		framingGroup,
		panelGroup,
		roofGroup,
		interiorGroup,
		exteriorGroup,
		frontVisibilityGroups,
		premiumPlacements: [],
	};
}

// ─── Calculation ────────────────────────────────────────────
function calculateBarrelMaterials(input: BuildInput): RawCalculationResult {
	const {
		dimensions: { width: W, length: L },
	} = input;
	const circumference = Math.PI * W; // barrel circumference
	const staveWidth = 0.09; // 90mm wide staves
	const numStaves = Math.ceil(circumference / staveWidth);
	const staveThick = 0.038; // 38mm thick

	const numBands = Math.max(2, Math.floor(L / 0.8));
	const { foundation, cradleRows } = resolveBarrelSupportLayout(W, L, input);
	const numCradles = cradleRows.length;
	const lmFoundationBeams = Math.ceil(foundation.totalBeamLengthLm * 1.05);
	const floorArea = W * 0.7 * L;
	const foundationArea = Math.ceil((W + 0.3) * (L + 0.3));
	const fastenerPackages = Math.max(
		2,
		Math.ceil((numStaves + numBands + numCradles) / 25),
	);
	const heaterRows: MaterialRow[] =
		input.thermalInputs.heaterType === "electric"
			? [
					{
						material: "Elaggregat",
						dimension: "Mellansegment",
						quantity: "1 st",
						phase: "systems",
						priceRuleId: "prices.elaggregat",
						materialKey: "heater.electric",
					},
					{
						material: "Aggregatsten",
						dimension: "20 kg",
						quantity: "1 st",
						phase: "systems",
						priceRuleId: "prices.aggregatsten",
						materialKey: "heater.stones",
					},
					{
						material: "Styrpanel",
						dimension: "Extern/integrerad",
						quantity: "1 st",
						phase: "systems",
						priceRuleId: "prices.styrpanel",
						materialKey: "heater.control-panel",
					},
				]
			: [
					{
						material: "Bastuaggregat",
						dimension: "Vedeldad kamin",
						quantity: "1 st",
						phase: "systems",
						priceRuleId: "prices.bastuaggregat",
						materialKey: "heater.wood",
					},
				];

	const materials: MaterialRow[] = [
		{
			material: "Betongplint 500mm",
			dimension: "Ø160mm",
			quantity: `${foundation.totalPiers} st`,
			phase: "foundation",
			priceRuleId: "prices.betongplint",
			materialKey: "foundation.plinth",
		},
		{
			material: "Stolpsko / infästning",
			dimension: "Grundinfästning",
			quantity: `${foundation.totalPiers} st`,
			phase: "foundation",
			priceRuleId: "prices.stolpsko",
			materialKey: "foundation.post-shoe",
		},
		{
			material: "Markduk",
			dimension: "Geotextil",
			quantity: `${foundationArea} m²`,
			phase: "foundation",
			priceRuleId: "prices.markduk",
			materialKey: "foundation.ground-fabric",
		},
		{
			material: "Dräneringsgrus / makadam",
			dimension: "Grundlager",
			quantity: `${foundationArea} m²`,
			phase: "foundation",
			priceRuleId: "prices.draneringsgrus",
			materialKey: "foundation.drainage-gravel",
		},
		{
			material: "Bärlina Tryckimpregnerad",
			dimension: "45×145 mm (NTR-A)",
			quantity: `${lmFoundationBeams} lpm`,
			phase: "foundation",
			priceRuleId: "prices.barlina",
			materialKey: "foundation.beam",
		},
		{
			material: "Stavvirke (Värmebehandlat)",
			dimension: `${staveThick * 1000}×90 mm`,
			quantity: `${numStaves} st × ${(L * 1000).toFixed(0)} mm = ${Math.ceil(numStaves * L)} lpm`,
			phase: "framing",
			priceRuleId: "prices.stavvirke",
			materialKey: "barrel.staves",
		},
		{
			material: "Gavlar (Limträlaminat)",
			dimension: "Diameter " + (W * 1000).toFixed(0) + "mm",
			quantity: "2 st",
			phase: "framing",
			priceRuleId: "prices.gavlar",
			materialKey: "barrel.end-caps",
		},
		{
			material: "Stålband",
			dimension: "30×3 mm",
			quantity: `${numBands} st`,
			phase: "framing",
			priceRuleId: "prices.stalband",
			materialKey: "barrel.steel-bands",
		},
		{
			material: "Vaggor / Stöd",
			dimension: "100×200 mm K-virke",
			quantity: `${numCradles} st`,
			phase: "foundation",
			priceRuleId: "prices.vaggor",
			materialKey: "barrel.cradles",
		},
		{
			material: "Golvbrädor (inre)",
			dimension: "28×120 mm",
			quantity: `${Math.ceil(floorArea)} m²`,
			phase: "interior",
			priceRuleId: "prices.golvbrädor",
			materialKey: "barrel.floor-boards",
		},
		{
			material: "Bastulav-virke (Kvistfritt)",
			dimension: "28×90 mm",
			quantity: `${Math.ceil(L * 0.7 * 2 * 5)} lpm`,
			phase: "interior",
			priceRuleId: "prices.lavvirke",
			materialKey: "interior.bench-timber",
		},
		{
			material: "Bastudörr (Glas)",
			dimension: "700×1600 mm",
			quantity: "1 st",
			phase: "systems",
			priceRuleId: "prices.bastuDorr",
			materialKey: "systems.door.sauna",
		},
		{
			material: "Ventiler",
			dimension: "Tilluft + Frånluft",
			quantity: "2 st",
			phase: "systems",
			priceRuleId: "prices.ventiler",
			materialKey: "systems.ventilation",
		},
		{
			material: "Rostfri skruv / beslagspaket",
			dimension: "Band, beslag och infästning",
			quantity: `${fastenerPackages} paket`,
			phase: "systems",
			priceRuleId: "prices.rostfriSkruv",
			materialKey: "systems.fasteners",
		},
		...heaterRows,
	];

	const cuts: CutRow[] = [
		{
			part: "Bärlinor (Grund, NTR-A 45×145)",
			count: `${foundation.numBeams} st`,
			cutLength: `${((W - 0.05) * 1000).toFixed(0)} mm`,
			materialKey: "foundation.beam",
		},
		{
			part: "Stavar (38×90)",
			count: `${numStaves} st`,
			cutLength: `${(L * 1000).toFixed(0)} mm`,
			materialKey: "barrel.staves",
		},
		{
			part: "Gavelskivor",
			count: "2 st",
			cutLength: `Ø${(W * 1000).toFixed(0)} mm (Cirkelsågad)`,
			materialKey: "barrel.end-caps",
		},
		{
			part: "Stålband",
			count: `${numBands} st`,
			cutLength: `${(circumference * 1000 + 200).toFixed(0)} mm (Inkl. överlapp)`,
			materialKey: "barrel.steel-bands",
		},
		{
			part: "Vaggor",
			count: `${numCradles} st`,
			cutLength: `${((W + 0.2) * 1000).toFixed(0)} mm (Profilsågad)`,
			materialKey: "barrel.cradles",
		},
		{
			part: "Golvbrädor (28×120)",
			// floorArea / 0.12 is running metres of 120 mm board — divide by
			// the board length for a piece count ("30 st × 2400 mm" claimed
			// 72 lpm where ~29 lpm of floor exists).
			count: `${Math.ceil(floorArea / 0.12 / Math.max(0.1, L - 0.1))} st`,
			cutLength: `${(L * 1000 - 100).toFixed(0)} mm`,
			materialKey: "barrel.floor-boards",
		},
	];

	return {
		materials,
		cuts,
		foundation,
		finishBasis: {
			// A tunnbastu has no ventilated facade — the staves ARE the exterior,
			// so the treatable area is the cylinder wrap plus both gable discs.
			// No batten figures: the barrel has no interior glespanel, so its
			// panel-direction swatch has no price to move (the staves' direction
			// is the barrel's geometry, not a finish choice).
			facadeAreaSqm: circumference * L + 2 * Math.PI * (W / 2) ** 2,
		},
	};
}

// ─── Camera ─────────────────────────────────────────────────
function getBarrelCameraPosition(W: number, L: number) {
	return createCameraPosition(
		{
			width: W + 0.2,
			height: W + 0.12,
			length: L,
		},
		[0.58, 0.32, 0.75],
		1.08,
		[0, 0.48, 0],
	);
}

function getBarrelCameraPresets(W: number, L: number) {
	const size = {
		width: W + 0.2,
		height: W + 0.12,
		length: L,
	};

	return [
		createCameraPreset(size, {
			id: "hero",
			label: "Hero",
			direction: [0.55, 0.29, 0.79],
			distanceMultiplier: 0.98,
			targetOffset: [0, 0.49, 0],
		}),
		createCameraPreset(size, {
			id: "side",
			label: "Sida",
			direction: [0.98, 0.19, 0.04],
			distanceMultiplier: 0.92,
			targetOffset: [0, 0.47, 0],
		}),
		createCameraPreset(size, {
			id: "interior-peek",
			label: "Interiör",
			direction: [0.23, 0.28, 0.93],
			distanceMultiplier: 0.76,
			targetOffset: [0, 0.51, -0.16],
		}),
	];
}

function formatGuideMaterial(row: {
	material: string;
	dimension: string;
	quantity: string;
}): BuildGuideText {
	return {
		materialRow: {
			material: row.material,
			dimension: row.dimension,
			quantity: row.quantity,
		},
	};
}

function formatGuideCut(row: {
	part: string;
	count: string;
	cutLength: string;
}): BuildGuideText {
	return {
		cutRow: { part: row.part, count: row.count, cutLength: row.cutLength },
	};
}

function summarizeGuideMaterials(
	context: BuildGuideModifierContext,
	materialKeys: string[],
	fallback: BuildGuideText,
): BuildGuideText[] {
	const rows = context.data.materials.filter(
		(row) => row.materialKey && materialKeys.includes(row.materialKey),
	);
	return rows.length > 0
		? rows.slice(0, 3).map((row) => formatGuideMaterial(row))
		: [fallback];
}

function summarizeGuideCuts(
	context: BuildGuideModifierContext,
	materialKeys: string[],
	fallback: BuildGuideText,
): BuildGuideText[] {
	const rows = context.data.cuts.filter(
		(row) => row.materialKey && materialKeys.includes(row.materialKey),
	);
	return rows.length > 0
		? rows.slice(0, 3).map((row) => formatGuideCut(row))
		: [fallback];
}

function modifyBarrelBuildGuideSteps(
	steps: ProjectBuildGuideStepDraft[],
	context: BuildGuideModifierContext,
): ProjectBuildGuideStepDraft[] {
	const framingStep = steps.find((step) => step.id === "framing");
	const roofStep = steps.find((step) => step.id === "roof");
	const shellTools = framingStep?.substeps[0]?.tools;
	const openingsTools = framingStep?.substeps[1]?.tools;
	const bandTools =
		roofStep?.substeps[2]?.tools ?? roofStep?.substeps[0]?.tools;
	const roofLayerLabel = context.data.roofAssembly?.layers.join(", ");

	return steps.map((step) => {
		if (step.id === "framing") {
			return {
				...step,
				title: buildGuideText("domain_build_guide_barrelShellTitle"),
				purpose: buildGuideText("domain_build_guide_barrelShellPurpose"),
				prerequisites: [buildGuideText("domain_build_guide_barrelShellPrereq")],
				substeps: [
					{
						id: "barrel-cradles",
						title: buildGuideText(
							"domain_build_guide_barrelShellSubstepCradles",
						),
						tools: shellTools,
						materialsSummary: summarizeGuideMaterials(
							context,
							["barrel.cradles"],
							buildGuideText("domain_build_guide_barrelShellCradleFallback"),
						),
						checkpoints: [
							{
								id: "barrel-cradles-level",
								label: buildGuideText(
									"domain_build_guide_barrelShellCheckpointCradlesLevel",
								),
							},
							{
								id: "barrel-cradles-spacing",
								label: buildGuideText(
									"domain_build_guide_barrelShellCheckpointCradlesSpacing",
								),
							},
						],
					},
					{
						id: "barrel-staves",
						title: buildGuideText(
							"domain_build_guide_barrelShellSubstepStaves",
						),
						crew: {
							size: "duo",
							reason: buildGuideText(
								"domain_build_guide_crewReasonBarrelStaves",
							),
						},
						tools: bandTools ?? shellTools,
						materialsSummary: summarizeGuideMaterials(
							context,
							["barrel.staves"],
							buildGuideText("domain_build_guide_barrelShellStavesFallback"),
						),
						checkpoints: [
							{
								id: "barrel-staves-starter",
								label: buildGuideText(
									"domain_build_guide_barrelShellCheckpointStavesStarter",
								),
							},
							{
								id: "barrel-staves-sequence",
								label: buildGuideText(
									"domain_build_guide_barrelShellCheckpointStavesSequence",
								),
							},
						],
					},
				],
				materialsSummary: [
					...summarizeGuideMaterials(
						context,
						["barrel.cradles"],
						buildGuideText("domain_build_guide_barrelShellCradleFallback"),
					),
					...summarizeGuideMaterials(
						context,
						["barrel.staves"],
						buildGuideText("domain_build_guide_barrelShellStavesFallback"),
					),
				],
				cutSummary: summarizeGuideCuts(
					context,
					["barrel.cradles", "barrel.staves"],
					buildGuideText("domain_build_guide_barrelShellCutsFallback"),
				),
				risks: [buildGuideText("domain_build_guide_barrelShellRiskTwist")],
				controls: [
					buildGuideText("domain_build_guide_barrelShellControlReference"),
				],
			};
		}

		if (step.id === "roof") {
			return {
				...step,
				title: buildGuideText("domain_build_guide_barrelRoofTitle"),
				purpose: buildGuideText("domain_build_guide_barrelRoofPurpose"),
				prerequisites: [buildGuideText("domain_build_guide_barrelRoofPrereq")],
				substeps: [
					{
						id: "barrel-roof-weather",
						title: buildGuideText(
							"domain_build_guide_barrelRoofSubstepWeather",
						),
						crew: {
							size: "duo",
							reason: buildGuideText(
								"domain_build_guide_crewReasonBarrelBands",
							),
						},
						tools: bandTools,
						checkpoints: [
							{
								id: "barrel-roof-weatherproof",
								label: roofLayerLabel
									? buildGuideText(
											"domain_build_guide_barrelRoofCheckpointWeather",
											{
												layers: roofLayerLabel,
											},
										)
									: buildGuideText(
											"domain_build_guide_barrelRoofCheckpointWeatherFallback",
										),
							},
						],
					},
				],
				materialsSummary:
					step.materialsSummary.length > 0
						? step.materialsSummary
						: [
								buildGuideText(
									"domain_build_guide_barrelRoofFallbackMaterials",
								),
							],
				cutSummary: summarizeGuideCuts(
					context,
					["barrel.staves"],
					buildGuideText("domain_build_guide_barrelRoofCutsFallback"),
				),
				risks: [buildGuideText("domain_build_guide_barrelRoofRiskWeather")],
				controls: [
					buildGuideText("domain_build_guide_barrelRoofControlWeather"),
				],
			};
		}

		if (step.id === "facade") {
			// The generic facade step may carry a treatment substep (issue 134) —
			// the tunnbastu buys the same paint for its staves. Replacing the
			// whole `substeps` array dropped it, so the barrel was billed for
			// slamfärg with no instructions for applying it.
			//
			// Matched on the checkpoint id, not the substep id: `createSubsteps`
			// overwrites substep ids positionally (`facade--substep-02`), so a
			// positional match would also drag in the cladding and door substeps
			// this modifier deliberately replaces. Checkpoint ids are authored
			// and stable.
			const treatmentSubsteps = step.substeps.filter((substep) =>
				substep.checkpoints.some(
					(checkpoint) => checkpoint.id === "facade-treatment-coverage",
				),
			);
			return {
				...step,
				title: buildGuideText("domain_build_guide_barrelEndsTitle"),
				purpose: buildGuideText("domain_build_guide_barrelEndsPurpose"),
				prerequisites: [buildGuideText("domain_build_guide_barrelEndsPrereq")],
				substeps: [
					{
						id: "barrel-end-caps",
						title: buildGuideText("domain_build_guide_barrelEndsSubstepCaps"),
						crew: {
							size: "duo",
							reason: buildGuideText("domain_build_guide_crewReasonBarrelEnds"),
						},
						tools: openingsTools,
						materialsSummary: summarizeGuideMaterials(
							context,
							["barrel.end-caps", "systems.door.sauna"],
							buildGuideText("domain_build_guide_barrelEndsFallbackMaterials"),
						),
						checkpoints: [
							{
								id: "barrel-end-caps-fit",
								label: buildGuideText(
									"domain_build_guide_barrelEndsCheckpointCaps",
								),
							},
							{
								id: "barrel-door-opening",
								label: buildGuideText(
									"domain_build_guide_barrelEndsCheckpointDoor",
								),
							},
						],
					},
					...treatmentSubsteps,
				],
				materialsSummary: summarizeGuideMaterials(
					context,
					["barrel.end-caps", "systems.door.sauna"],
					buildGuideText("domain_build_guide_barrelEndsFallbackMaterials"),
				),
				cutSummary: summarizeGuideCuts(
					context,
					["barrel.end-caps"],
					buildGuideText("domain_build_guide_barrelEndsFallbackCuts"),
				),
				risks: [buildGuideText("domain_build_guide_barrelEndsRiskRound")],
				controls: [
					buildGuideText("domain_build_guide_barrelEndsControlMeasure"),
				],
			};
		}

		if (step.id === "insulation") {
			return {
				...step,
				title: buildGuideText("domain_build_guide_barrelBandsTitle"),
				purpose: buildGuideText("domain_build_guide_barrelBandsPurpose"),
				prerequisites: [buildGuideText("domain_build_guide_barrelBandsPrereq")],
				substeps: [
					{
						id: "barrel-bands-tension",
						title: buildGuideText(
							"domain_build_guide_barrelBandsSubstepTension",
						),
						crew: {
							size: "duo",
							reason: buildGuideText(
								"domain_build_guide_crewReasonBarrelBands",
							),
						},
						tools: bandTools,
						materialsSummary: summarizeGuideMaterials(
							context,
							["barrel.steel-bands"],
							buildGuideText("domain_build_guide_barrelBandsFallbackMaterials"),
						),
						checkpoints: [
							{
								id: "barrel-bands-even",
								label: buildGuideText(
									"domain_build_guide_barrelBandsCheckpointEven",
								),
							},
						],
					},
					{
						id: "barrel-bands-retighten",
						title: buildGuideText(
							"domain_build_guide_barrelBandsSubstepRetighten",
						),
						checkpoints: [
							{
								id: "barrel-bands-retighten-window",
								label: buildGuideText(
									"domain_build_guide_barrelBandsCheckpointRetighten",
								),
							},
						],
					},
				],
				materialsSummary: summarizeGuideMaterials(
					context,
					["barrel.steel-bands"],
					buildGuideText("domain_build_guide_barrelBandsFallbackMaterials"),
				),
				cutSummary: summarizeGuideCuts(
					context,
					["barrel.steel-bands"],
					buildGuideText("domain_build_guide_barrelBandsFallbackCuts"),
				),
				risks: [buildGuideText("domain_build_guide_barrelBandsRiskTension")],
				controls: [
					buildGuideText("domain_build_guide_barrelBandsControlSequence"),
					buildGuideText("domain_build_guide_barrelBandsControlRetighten"),
				],
			};
		}

		return step;
	});
}

// ─── Export Engine ───────────────────────────────────────────
export const barrelSaunaEngine: RawSaunaEngine = {
	label: "Bastutunna",
	icon: "🛢️",
	description: "Traditionell tunnformad bastu av stavvirke",
	defaultDimensions: BARREL_DEFAULT_DIMENSIONS,
	buildSauna3D: (input) => buildBarrel3D(input),
	calculateMaterials: (input) => calculateBarrelMaterials(input),
	getAuditMetadata: () => getBarrelAuditMetadata(),
	getEvidenceConfig: (input, calculation) =>
		getBarrelEvidenceConfig(input, calculation),
	getDefaultCameraPosition: getBarrelCameraPosition,
	getCameraPresets: (input) =>
		getBarrelCameraPresets(input.dimensions.width, input.dimensions.length),
	modifyBuildGuideSteps: modifyBarrelBuildGuideSteps,
};
